import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useEarningsInsightsFilter } from '~/hooks/use-earnings-insights-filter/useEarningsInsightsFilter';
import { useEntity } from '~/hooks/use-entities';
import { useGetEarningsInsightPreview } from '~/hooks/use-get-earnings-insight-preview/useGetEarningsInsightPreview';

import { useEarningsInsightDrawer } from '../../useEarningsInsightDrawer';
import { BullBearPoints } from '../components/bull-bear-points/BullBearPoints';
import { CitationsSection } from '../components/citations-section/CitationsSection';
import { EarningsInsightHeader } from '../components/earnings-insight-header/EarningsInsightHeader';
import { EarningsInsightTypePill } from '../components/earnings-insight-type-pill/EarningsInsightTypePill';
import { Sidebar } from '../components/side-bar/Sidebar';
import { useSidebarItems } from '../components/side-bar/useSidebarItems';
import { TextSection } from '../components/text-section/TextSection';
import { ThematicFocus } from '../components/thematic-focus/ThematicFocus';
import * as Shared from '../EarningsInsightWidget.styles';
import { EpsExpectationsSection } from './components/eps-expectations-section/EpsExpectationsSection';
import { ImpactedCompanies } from './components/impacted-companies/ImpactedCompanies';
import { KpisTable } from './components/kpis-table/KpisTable';
import { PreviewOverview } from './components/PreviewOverview';
import * as S from './EarningsPreview.styles';

export const EarningsPreview = () => {
  const { t } = useTranslation(['earnings']);
  const containerRef = useRef<HTMLDivElement>(null);
  const { activeEntityTag, earningsId } = useEarningsInsightDrawer();

  const {
    data: earningsInsightsData,
    isFetching: isEarningsInsightsFetching,
    isError: isEarningsInsightsError,
    error: earningsInsightsError,
  } = useEarningsInsightsFilter({
    body: {
      earnings_ids: [earningsId ?? ''],
    },
    isEnabled: !!earningsId,
  });

  const {
    data: entityData,
    isFetching,
    isError: isEntityError,
    error,
  } = useEntity(activeEntityTag ?? '');
  const { previewItems } = useSidebarItems();

  const {
    data: previewData,
    isFetching: isPreviewFetching,
    isError: isPreviewError,
    error: previewError,
  } = useGetEarningsInsightPreview(
    earningsInsightsData?.find(insight => insight.type === 'preview')?.id ?? ''
  );

  const isLoading =
    isFetching || isPreviewFetching || isEarningsInsightsFetching;

  const isError = isPreviewError || isEarningsInsightsError || isEntityError;

  if (isLoading) {
    return <Shared.StyledLoader />;
  }

  if (isError || !entityData?.data) {
    throw new Error(
      error?.message || previewError?.message || earningsInsightsError?.message
    );
  }

  if (!previewData) {
    return (
      <Shared.Container>
        <Shared.EmptyWrapper>
          <Shared.StyledImg
            alt=""
            src="/app/static/images/empty-state/radio-telescope.png"
            height={130}
            width={146}
          />
          <Shared.EmptyTitle>
            {t('earnings:earningsInsight.drawer.earningsPreview.emptyTitle')}
          </Shared.EmptyTitle>
        </Shared.EmptyWrapper>
      </Shared.Container>
    );
  }

  const entity = entityData.data;

  return (
    <S.Wrapper>
      <EarningsInsightTypePill
        entityTag={activeEntityTag ?? ''}
        earningsInsight={earningsInsightsData?.find(
          insight => insight.type === 'preview'
        )}
        insightType={previewData.type}
        createdAt={previewData.created_at}
        updatedAt={previewData.updated_at}
      />
      <S.Separator>
        <S.LeftContainer>
          <EarningsInsightHeader entity={entity} />
          <S.ScrollableContainer ref={containerRef}>
            <S.Section ref={previewItems[0].ref} data-testid="preview-overview">
              <PreviewOverview
                title={previewData.title}
                activeEntityTag={activeEntityTag ?? ''}
              />
            </S.Section>
            <S.Section
              ref={previewItems[1].ref}
              data-testid="earnings-reaction-scenario"
            >
              <EpsExpectationsSection
                earningsId={earningsId ?? ''}
                entity={entity}
              />
              <S.TextSectionWrapper>
                <TextSection
                  useBulletPoints
                  variant="secondary"
                  title={t(
                    'earningsInsight.drawer.earningsPreview.sentimentAndExpectationsSetup'
                  )}
                  textContent={previewData.historical_context}
                />
                <TextSection
                  useBulletPoints
                  variant="secondary"
                  title={t(
                    'earningsInsight.drawer.earningsPreview.seasonality'
                  )}
                  textContent={previewData.seasonality}
                />
              </S.TextSectionWrapper>
            </S.Section>
            <S.Section ref={previewItems[2].ref} data-testid="kpis">
              <KpisTable kpisData={previewData.kpis} />
            </S.Section>
            <S.Section ref={previewItems[3].ref} data-testid="thematic-focus">
              <ThematicFocus
                themes={previewData.themes}
                otherThemes={previewData.other_themes}
                title={t(
                  'earningsInsight.drawer.earningsPreview.items.thematicFocusPreview'
                )}
              />
            </S.Section>
            <S.Section ref={previewItems[4].ref} data-testid="key-items">
              <TextSection
                useBulletPoints
                variant="primary"
                title={t(
                  'earningsInsight.drawer.earningsPreview.items.keyItems'
                )}
                textContent={previewData.key_items}
              />
            </S.Section>
            <S.Section
              ref={previewItems[5].ref}
              data-testid="investment-outlook"
            >
              <BullBearPoints
                data={previewData.investment_outlook}
                title={t(
                  'earningsInsight.drawer.earningsPreview.items.investmentOutlook'
                )}
              />
            </S.Section>
            <S.Section
              ref={previewItems[6].ref}
              data-testid="impacted-companies"
            >
              <ImpactedCompanies
                impactedCompanies={previewData.impacted_companies}
              />
            </S.Section>
            <S.Section data-testid="citations">
              {previewData?.citations?.length > 0 && (
                <CitationsSection
                  title={t('earningsInsight.drawer.webLinks')}
                  count={previewData.citations.length ?? 0}
                  items={previewData.citations ?? []}
                />
              )}
            </S.Section>
          </S.ScrollableContainer>
        </S.LeftContainer>
        <Sidebar
          entityData={entity}
          containerRef={containerRef}
          items={previewItems}
        />
      </S.Separator>
    </S.Wrapper>
  );
};
